import { useState } from 'react';
import { Icon } from './Icons.jsx';
import { PASSWORD_RULES, PasswordRules } from './PasswordRules.jsx';
import LegalModal from './LegalModal.jsx';
import CountryPhoneInput from './CountryPhoneInput.jsx';
import { maskCPF, isValidCPF } from '../utils/format.js';
import { DEFAULT_COUNTRY, getCountry } from '../utils/countries.js';

// ─── componente auxiliar: Field ──────────────────────────────────────────────

function Field({ label, valid, error, children }) {
  return (
    <div>
      <label className="input-label">{label}</label>
      <div style={{ position: 'relative' }}>
        {children}
        {valid && !error && (
          <span style={{ position: 'absolute', right: 14, top: 16, pointerEvents: 'none' }}>
            <Icon.check size={18} color="var(--success)" strokeWidth={2.5}/>
          </span>
        )}
      </div>
    </div>
  );
}

// ─── SignUpScreen ─────────────────────────────────────────────────────────────

export default function SignUpScreen({ onBack, onCreated }) {
  const [form, setForm] = useState({
    name: '', cpf: '', phone: '', country: DEFAULT_COUNTRY, email: '', password: '', confirm: '',
  });
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [touchedConfirm, setTouchedConfirm] = useState(false);
  const [accept, setAccept] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [legalDoc, setLegalDoc] = useState(null);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const ruleState = PASSWORD_RULES.map(r => ({ ...r, ok: r.test(form.password) }));
  const passwordValid = ruleState.every(r => r.ok);
  const confirmValid  = form.confirm.length > 0 && form.confirm === form.password;
  const confirmError  = touchedConfirm && form.confirm.length > 0 && form.confirm !== form.password;

  const nameValid  = form.name.trim().split(/\s+/).length >= 2 && form.name.trim().length >= 5;
  const cpfValid   = isValidCPF(form.cpf);
  const cpfError   = form.cpf.replace(/\D/g, '').length === 11 && !cpfValid;
  const phoneDigits = form.phone.replace(/\D/g, '');
  const phoneValid = phoneDigits.length >= (form.country === 'BR' ? 10 : 8);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);

  const canSubmit = nameValid && cpfValid && phoneValid && emailValid
                 && passwordValid && confirmValid && accept;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setApiError('');
    setLoading(true);
    try {
      await onCreated({ ...form, phone: getCountry(form.country).dial + phoneDigits });
    } catch (err) {
      setApiError(err.message || 'Não foi possível criar a conta. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="screen" style={{ background: 'var(--bg)' }}>
      {/* hero compacto */}
      <div style={{
        background: 'linear-gradient(160deg, var(--midnight) 0%, var(--midnight-90) 55%, var(--navy-3) 100%)',
        color: 'var(--white)', padding: '58px 22px 26px', position: 'relative', overflow: 'hidden',
        flexShrink: 0,
      }}>
        <svg width="240" height="240" style={{ position: 'absolute', right: -90, top: -70, opacity: 0.15 }}>
          {[...Array(6)].map((_, i) => (
            <circle key={i} cx="120" cy="120" r={22 + i * 18} stroke="var(--brand-primary)" strokeWidth="1" fill="none"/>
          ))}
        </svg>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <button onClick={onBack} aria-label="Voltar" style={{
            width: 38, height: 38, borderRadius: 99,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--overlay-white-12)', border: '1px solid var(--overlay-white-20)',
          }}>
            <Icon.chevL size={20} color="var(--white)"/>
          </button>
          <h1 style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.15, marginTop: 18, color: 'var(--white)' }}>
            Criar conta
          </h1>
          <p style={{ marginTop: 6, fontSize: 13.5, color: 'var(--overlay-white-72)' }}>
            Leva menos de 1 minuto. Você precisa de uma conta para comprar exames.
          </p>
        </div>
      </div>

      {/* form */}
      <div style={{ flex: 1, padding: '22px 22px 28px', display: 'flex', flexDirection: 'column' }}>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {apiError && (
            <div style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--danger-tint)', color: 'var(--danger-strong)', fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
              <Icon.info size={16} color="var(--danger-strong)"/>{apiError}
            </div>
          )}

          <Field label="Nome completo" valid={nameValid && form.name.length > 0}>
            <Icon.user size={18} color="var(--text-subtle)" style={{ position: 'absolute', left: 14, top: 16 }}/>
            <input
              className="input"
              value={form.name}
              onChange={set('name')}
              placeholder="NOME COMPLETO"
              autoComplete="name"
              style={{ paddingLeft: 42 }}
            />
          </Field>

          <Field label="CPF" valid={cpfValid} error={cpfError}>
            <input
              className="input"
              value={form.cpf}
              onChange={(e) => setForm(f => ({ ...f, cpf: maskCPF(e.target.value) }))}
              placeholder="000.000.000-00"
              inputMode="numeric"
              autoComplete="off"
            />
            {cpfError && (
              <div role="alert" style={{ marginTop: 4, fontSize: 12, color: 'var(--danger)' }}>
                CPF inválido.
              </div>
            )}
          </Field>

          <div>
            <label className="input-label">Celular</label>
            <CountryPhoneInput
              country={form.country}
              onCountryChange={(country) => setForm(f => ({ ...f, country }))}
              value={form.phone}
              onChange={(phone) => setForm(f => ({ ...f, phone }))}
            />
          </div>

          <Field label="E-mail" valid={emailValid && form.email.length > 0}>
            <Icon.mail size={18} color="var(--text-subtle)" style={{ position: 'absolute', left: 14, top: 16 }}/>
            <input
              className="input"
              type="email"
              value={form.email}
              onChange={set('email')}
              placeholder="seu@email.com"
              autoComplete="email"
              style={{ paddingLeft: 42 }}
            />
          </Field>

          <Field label="Senha">
            <Icon.lock size={18} color="var(--text-subtle)" style={{ position: 'absolute', left: 14, top: 16 }}/>
            <input
              className="input"
              type={showPwd ? 'text' : 'password'}
              value={form.password}
              onChange={set('password')}
              placeholder="Crie uma senha forte"
              autoComplete="new-password"
              style={{ paddingLeft: 42, paddingRight: 48 }}
            />
            <button
              type="button"
              aria-label={showPwd ? 'Ocultar senha' : 'Mostrar senha'}
              onClick={() => setShowPwd(v => !v)}
              style={{ position: 'absolute', right: 10, top: 13, padding: 6, color: 'var(--text-muted)' }}
            >
              {showPwd ? <Icon.eyeOff size={20}/> : <Icon.eye size={20}/>}
            </button>
          </Field>

          <PasswordRules rules={ruleState} active={form.password.length > 0}/>

          <Field label="Confirmar senha" valid={confirmValid} error={confirmError}>
            <Icon.lock size={18} color="var(--text-subtle)" style={{ position: 'absolute', left: 14, top: 16 }}/>
            <input
              className="input"
              type={showConfirm ? 'text' : 'password'}
              value={form.confirm}
              onChange={set('confirm')}
              onBlur={() => setTouchedConfirm(true)}
              placeholder="Repita a senha"
              autoComplete="new-password"
              style={{ paddingLeft: 42, paddingRight: 48 }}
            />
            <button
              type="button"
              aria-label={showConfirm ? 'Ocultar senha' : 'Mostrar senha'}
              onClick={() => setShowConfirm(v => !v)}
              style={{ position: 'absolute', right: 10, top: 13, padding: 6, color: 'var(--text-muted)' }}
            >
              {showConfirm ? <Icon.eyeOff size={20}/> : <Icon.eye size={20}/>}
            </button>
          </Field>

          {confirmError && (
            <div role="alert" style={{ marginTop: -8, fontSize: 12, color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon.info size={14} color="var(--danger)"/> As senhas não conferem.
            </div>
          )}

          {/* checkbox de termos */}
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 4, cursor: 'pointer' }}>
            <span
              onClick={() => setAccept(v => !v)}
              style={{
                width: 20, height: 20, borderRadius: 6, flexShrink: 0, marginTop: 1,
                border: '1.5px solid ' + (accept ? 'var(--brand-primary)' : 'var(--border-strong)'),
                background: accept ? 'var(--brand-primary)' : 'var(--white)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s',
              }}
            >
              {accept && <Icon.check size={14} color="var(--white)" strokeWidth={3}/>}
            </span>
            <span onClick={() => setAccept(v => !v)} style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
              Eu li e aceito os{' '}
              <a href="#" onClick={(e) => { e.stopPropagation(); e.preventDefault(); setLegalDoc('termos'); }} style={{ color: 'var(--brand-primary)', fontWeight: 600, textDecoration: 'none' }}>
                Termos de uso
              </a>
              {' '}e a{' '}
              <a href="#" onClick={(e) => { e.stopPropagation(); e.preventDefault(); setLegalDoc('privacidade'); }} style={{ color: 'var(--brand-primary)', fontWeight: 600, textDecoration: 'none' }}>
                Política de privacidade
              </a>.
            </span>
          </label>

          <button type="submit" disabled={!canSubmit || loading} className="btn btn-primary" style={{ marginTop: 8 }}>
            {loading ? 'Criando conta…' : <>Criar minha conta <Icon.arrowR size={18} color={canSubmit ? 'var(--white)' : 'var(--gray-disabled-text)'}/></>}
          </button>
        </form>

        <div style={{ flex: 1, background: 'var(--bg)' }}/>
        <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-muted)', marginTop: 18, paddingBottom: 4 }}>
          Já tem conta?{' '}
          <a href="#" onClick={(e) => { e.preventDefault(); onBack?.(); }}
            style={{ color: 'var(--brand-primary)', fontWeight: 600, textDecoration: 'none' }}>
            Entrar
          </a>
        </div>
      </div>

      {legalDoc && <LegalModal doc={legalDoc} onClose={() => setLegalDoc(null)}/>}
    </div>
  );
}
